/**
 * Places service - handles location-based services and Google Places API
 */

import { BaseApiClient } from '../core/base-client';
import { PlaceInfo, NearbyPlacesResponse, NearbyPlace } from '../types';

export class PlacesService {
  constructor(private client: BaseApiClient) {}

  /**
   * Fetch nearby places using Google Places API
   */
  async fetchNearbyPlaces(
    latitude: number,
    longitude: number,
    radius: number = 1000,
    types: string = 'restaurant|subway_station|train_station|bus_station|tourist_attraction'
  ): Promise<PlaceInfo[]> {
    try {
      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        radius: radius.toString(),
        types
      });

      const response = await this.client.get<NearbyPlacesResponse>(`api/places/nearby?${params}`);
      
      if (response.status !== 'OK') {
        console.warn('Places API returned status:', response.status);
        return [];
      }

      return response.places || [];
    } catch (error) {
      console.error('Error fetching nearby places:', error);
      return [];
    }
  }

  /**
   * Convert PlaceInfo to NearbyPlace format
   */
  convertToNearbyPlace(place: PlaceInfo): NearbyPlace {
    const getIcon = (category: string): NearbyPlace['icon'] => {
      if (category.includes('subway') || category.includes('train')) return 'train';
      if (category.includes('bus')) return 'bus';
      if (category.includes('restaurant')) return 'restaurant';
      if (category.includes('cafe')) return 'cafe';
      return 'attraction';
    };

    const getType = (category: string): NearbyPlace['type'] => {
      if (category.includes('subway') || category.includes('train') || category.includes('bus')) return 'transit';
      if (category.includes('restaurant')) return 'restaurant';
      if (category.includes('cafe')) return 'cafe';
      return 'tourist_attraction';
    };

    return {
      id: place.placeId,
      name: place.name,
      address: place.address,
      walkingTime: `${place.walkingTimeMinutes} min`,
      distance: place.distance,
      type: getType(place.category),
      icon: getIcon(place.category),
      rating: place.rating,
      priceLevel: place.priceLevel === '$' ? 1 : place.priceLevel === '$$' ? 2 : place.priceLevel === '$$$' ? 3 : place.priceLevel === '$$$$' ? 4 : undefined,
      isOpen: place.isOpen,
      photoUrl: place.photoUrl
    };
  }
}

/**
 * Factory function to create places service
 */
export function createPlacesService(client: BaseApiClient): PlacesService {
  return new PlacesService(client);
} 